import React, { useEffect, useRef, useState } from 'react';
import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  ArrowsPointingOutIcon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  ChevronLeftIcon,
  CodeBracketIcon,
  Cog6ToothIcon,
  DocumentDuplicateIcon,
  EllipsisHorizontalIcon,
  HandThumbDownIcon,
  HandThumbUpIcon,
  MoonIcon,
  PencilSquareIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import {
  useInView,
  usePlayback,
  usePrefersReducedMotion,
  useSmoothCoworkScroll,
} from '../pbi-development/coworkChatHooks';
import { QuerySubmitButton } from './DynamicVisualizationPanel';
import SemanticHRPbiPanel, { resolvePbiBookmarkState, resolvePbiDashboardView } from './SemanticHRPbiPanel';
import {
  SEMHR_CHAT_HISTORY,
  SEMHR_SUGGESTED,
  SEMHR_TOUR,
  SEMHR_TOUR_DELAYS,
  SEMHR_TOUR_LOOP_PAUSE,
  SEMHR_TOUR_STEPS,
} from './semanticHrTourData';
import {
  HiresTermsChartSvg,
} from './semanticHrTourCharts';
import { BRAND } from '../../../../content/staticAssets';

function easeInOutQuint(t) {
  return t < 0.5 ? 16 * t ** 5 : 1 - (-2 * t + 2) ** 5 / 2;
}

function useSmoothScrollTo(
  scrollRef,
  targetY,
  enabled,
  reducedMotion,
  { minDuration = 550, maxDuration = 1200, delay = 0 } = {},
) {
  const rafRef = useRef(null);
  const animRef = useRef(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !enabled) return undefined;

    const target = Math.max(0, targetY);
    if (reducedMotion) {
      el.scrollTop = target;
      return undefined;
    }

    const startScroll = () => {
      const start = el.scrollTop;
      const distance = target - start;
      if (Math.abs(distance) < 2) return;

      const duration = Math.min(maxDuration, Math.max(minDuration, Math.abs(distance) * 1.35));
      const active = animRef.current;
      if (active) {
        const activeTarget = active.start + active.distance;
        if (Math.abs(activeTarget - target) < 12) {
          active.start = start;
          active.distance = distance;
          active.startTime = performance.now();
          active.duration = duration;
          return;
        }
        cancelAnimationFrame(rafRef.current);
      }

      const startTime = performance.now();
      animRef.current = { start, distance, startTime, duration };

      const step = (now) => {
        const anim = animRef.current;
        if (!anim) return;
        const p = Math.min((now - anim.startTime) / anim.duration, 1);
        el.scrollTop = anim.start + anim.distance * easeInOutQuint(p);
        if (p < 1) rafRef.current = requestAnimationFrame(step);
        else animRef.current = null;
      };
      rafRef.current = requestAnimationFrame(step);
    };

    let delayTimer;
    if (delay > 0) delayTimer = setTimeout(startScroll, delay);
    else startScroll();

    return () => {
      if (delayTimer) clearTimeout(delayTimer);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      animRef.current = null;
    };
  }, [scrollRef, targetY, enabled, reducedMotion, minDuration, maxDuration, delay]);
}

const TOUR_DESIGN_WIDTH = 1040;
const TOUR_DESIGN_HEIGHT = TOUR_DESIGN_WIDTH / 2.05;

function useTourViewportScale(viewportRef) {
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return undefined;

    const update = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (!w || !h) return;
      const scale = Math.max(w / TOUR_DESIGN_WIDTH, h / TOUR_DESIGN_HEIGHT);
      el.style.setProperty('--semhr-scale', String(scale));
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [viewportRef]);
}

function measureDvScrollTarget(el, phase) {
  const anchor = el.querySelector('.ia-semhr-dv-anchor');
  if (!anchor) return null;

  const dvTop = anchor.offsetTop;

  if (phase === 'line') {
    return { target: Math.max(0, el.scrollHeight - el.clientHeight), min: 900, max: 2000, delay: 80 };
  }

  if (phase === 'heatmap') {
    const heatmap = el.querySelector('.ia-semhr-heatmap-card');
    if (heatmap) {
      const cardTop = dvTop + heatmap.offsetTop;
      const cardBottom = cardTop + heatmap.offsetHeight;
      let target = Math.max(0, cardTop - 8);
      if (cardBottom > target + el.clientHeight - 8) {
        target = Math.max(0, cardBottom - el.clientHeight + 8);
      }
      return { target, min: 700, max: 1400, delay: 0 };
    }
  }

  if (phase === 'entry') {
    return { target: Math.max(0, dvTop - 8), min: 1200, max: 2800, delay: 120 };
  }

  return null;
}

function useStableDvScroll(scrollRef, phase, enabled, reducedMotion) {
  const [scrollState, setScrollState] = useState({ target: 0, min: 550, max: 1200, delay: 0 });

  useEffect(() => {
    if (!enabled || !phase) {
      setScrollState({ target: 0, min: 550, max: 1200, delay: 0 });
      return undefined;
    }

    const el = scrollRef.current;
    if (!el) return undefined;

    let cancelled = false;
    const timers = [];

    const commit = () => {
      if (cancelled) return;
      const measured = measureDvScrollTarget(el, phase);
      if (!measured) return;
      setScrollState((current) => {
        if (Math.abs(current.target - measured.target) < 8) return current;
        return measured;
      });
    };

    const scheduleMeasure = (delayMs = 0) => {
      const id = setTimeout(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(commit);
        });
      }, delayMs);
      timers.push(id);
    };

    scheduleMeasure(0);
    if (phase === 'entry') {
      scheduleMeasure(140);
      scheduleMeasure(300);
    }

    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, [scrollRef, phase, enabled]);

  useSmoothScrollTo(
    scrollRef,
    scrollState.target,
    enabled,
    reducedMotion,
    { minDuration: scrollState.min, maxDuration: scrollState.max, delay: scrollState.delay },
  );
}

function resolveDynamicPhase(isVisible) {
  if (isVisible('line-chart')) return 'line';
  if (isVisible('line-edit')) return 'heatmap-edit';
  if (isVisible('heatmap')) return 'heatmap';
  if (isVisible('dynamic-prompt')) return 'prompt';
  if (isVisible('dynamic-empty')) return 'empty';
  return null;
}

function getCurrentStep(visibleCount) {
  if (visibleCount <= 0) return null;
  return SEMHR_TOUR_STEPS[Math.min(visibleCount - 1, SEMHR_TOUR_STEPS.length - 1)];
}

function Step({ show, children }) {
  if (!show) return null;
  return <div className="ia-semhr-block">{children}</div>;
}

function SegmentedSwitch({ value, options, className = '' }) {
  return (
    <div className={`inline-flex items-center rounded-full border border-ink-200/90 bg-surface-subtle/60 p-0.5 ${className}`}>
      {options.map((opt) => {
        const active = value === opt.id;
        return (
          <span
            key={opt.id}
            className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 font-semibold leading-none transition-colors ${
              active ? 'bg-white text-brand-700 shadow-sm' : 'text-ink-500'
            }`}
          >
            {opt.icon ? <span className="inline-flex shrink-0 items-center justify-center">{opt.icon}</span> : null}
            {opt.label}
          </span>
        );
      })}
    </div>
  );
}

function TopBar() {
  return (
    <header className="grid shrink-0 grid-cols-3 items-center border-b border-ink-200/80 bg-white px-1.5 py-1">
      <div aria-hidden="true" />
      <div className="flex justify-center">
        <div className="flex items-center gap-0.5 rounded-full border border-ink-200/90 bg-surface-subtle/60 p-0.5 text-[8px]">
          <span className="rounded-full bg-brand-700 px-2 py-0.5 font-semibold text-white">Copilot</span>
          <span className="rounded-full px-2 py-0.5 font-semibold text-ink-500">Builder</span>
        </div>
      </div>
      <div className="flex items-center justify-end gap-0.5 text-ink-400">
        <Cog6ToothIcon className="h-2.5 w-2.5" aria-hidden="true" />
        <MoonIcon className="h-2.5 w-2.5" aria-hidden="true" />
      </div>
    </header>
  );
}

function Sidebar() {
  return (
    <div className="ia-semhr-sidebar flex shrink-0 flex-col border-r border-ink-200/80 bg-surface-subtle/40">
      <div className="flex items-center gap-1.5 border-b border-ink-100 px-1.5 py-1.5">
        <img
          src={BRAND.logoMark}
          alt=""
          aria-hidden="true"
          className="h-8 w-8 shrink-0 rounded object-contain"
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
        <div className="min-w-0 flex-1 overflow-visible">
          <p className="whitespace-nowrap text-[11px] font-semibold leading-tight text-ink-800">Semantic HR</p>
          <p className="whitespace-nowrap text-[8px] leading-tight text-ink-500">Copilot Analyst</p>
        </div>
        <ChevronLeftIcon className="h-3 w-3 shrink-0 text-ink-400" aria-hidden="true" />
      </div>
      <div className="px-2 py-1">
        <span className="flex w-full items-center gap-1 rounded-md px-1 py-1 text-[8px] font-medium leading-none text-ink-700">
          <PencilSquareIcon className="h-3 w-3 shrink-0 text-ink-500" aria-hidden="true" />
          <span>New chat</span>
        </span>
      </div>
      <div className="min-h-0 flex-1 overflow-hidden px-2">
        <p className="mb-0.5 text-[7px] font-semibold uppercase tracking-wide text-ink-400">History</p>
        <ul className="space-y-px">
          {SEMHR_CHAT_HISTORY.map((item) => (
            <li key={item.title} className={`flex items-start justify-between gap-0.5 rounded px-1 py-0.5 text-[8px] leading-snug ${item.active ? 'bg-brand-50 text-brand-800 ring-1 ring-brand-100' : 'text-ink-600'}`}>
              <div className="min-w-0">
                <p className="truncate font-medium">{item.title}</p>
                <p className="truncate text-[7px] text-ink-400">{item.date}</p>
              </div>
              <EllipsisHorizontalIcon className="h-2.5 w-2.5 shrink-0 text-ink-400" aria-hidden="true" />
            </li>
          ))}
        </ul>
      </div>
      <div className="border-t border-ink-100 px-2 py-1.5">
        <p className="mb-0.5 text-[7px] font-semibold uppercase tracking-wide text-ink-400">Workspace</p>
        <div className="flex items-center gap-1 rounded border border-ink-200/80 bg-white px-1.5 py-1">
          <Squares2X2Icon className="h-3 w-3 shrink-0 text-brand-600" aria-hidden="true" />
          <div className="min-w-0 leading-none">
            <p className="truncate text-[8px] font-semibold text-ink-800">Builder</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function LandingPanel() {
  return (
    <div className="flex min-h-0 flex-1 flex-col items-center justify-center overflow-hidden px-2 py-2 text-center">
      <img src={BRAND.logoMark} alt="" aria-hidden="true" className="mb-2 h-8 w-8 object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
      <h3 className="text-[11px] font-semibold text-ink-900">Copilot Analyst</h3>
      <p className="mt-1 max-w-[320px] text-[8px] leading-relaxed text-ink-500">
        Ask natural language questions about workforce data. Get SQL-backed answers, tabular results, and linked Power BI dashboards.
      </p>
      <span className="mt-2 rounded-full border border-ink-200/90 px-2 py-0.5 text-[7px] font-medium text-ink-600">SuccessFactors</span>
      <p className="mt-4 text-[7px] font-semibold uppercase tracking-wide text-ink-400">Suggested questions</p>
      <div className="mt-2 grid w-full max-w-[340px] grid-cols-2 gap-1.5">
        {SEMHR_SUGGESTED.map((item) => (
          <div key={item.tag} className="rounded-lg border border-ink-200/90 bg-white px-2 py-1.5 text-left">
            <p className="text-[7px] font-semibold uppercase tracking-wide text-brand-600">{item.tag}</p>
            <p className="mt-0.5 text-[8px] leading-snug text-ink-700">{item.q}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function Composer({ isLoading = false }) {
  return (
    <div className="shrink-0 border-t border-ink-100 bg-white px-2 py-1.5">
      <div className="rounded-xl border border-ink-200/90 px-2 py-1.5">
        <p className="text-[8px] text-ink-400">Ask about headcount, attrition, hiring, workforce planning…</p>
        <div className="mt-1 flex items-center justify-between gap-1">
          <div className="flex min-w-0 flex-nowrap items-center gap-1">
            <span className="shrink-0 rounded-full border border-ink-200/90 px-1.5 py-px text-[7px] whitespace-nowrap text-ink-600">Ask</span>
            <span className="shrink-0 rounded-full border border-ink-200/90 px-1.5 py-px text-[7px] whitespace-nowrap text-ink-600">SemanticHR Copilot Analyst</span>
            <span className="shrink-0 rounded-full border border-ink-200/90 px-1.5 py-px text-[7px] whitespace-nowrap text-ink-600">Mistral Large</span>
          </div>
          <QuerySubmitButton state={isLoading ? 'loading' : 'ready'} size="md" />
        </div>
      </div>
      <p className="mt-1 text-center text-[7px] text-ink-400">Copilot uses Snowflake Cortex. Results are governed by your HR data model.</p>
    </div>
  );
}

function ChartSqlToggles({ sqlMode }) {
  return (
    <div className="flex justify-end">
      <SegmentedSwitch
        value={sqlMode ? 'sql' : 'chart'}
        options={[
          { id: 'chart', label: 'Chart', icon: <ChartBarIcon className="h-2 w-2" aria-hidden="true" /> },
          { id: 'sql', label: 'SQL', icon: <CodeBracketIcon className="h-2 w-2" aria-hidden="true" /> },
        ]}
        className="text-[7px]"
      />
    </div>
  );
}

function HiresTermsChart() {
  return (
    <div className="overflow-hidden rounded-md border border-ink-200/90 bg-white">
      <div className="flex items-center justify-between border-b border-ink-100 px-2 py-1">
        <p className="text-[8px] font-semibold text-ink-800">{SEMHR_TOUR.chart.title}</p>
        <span className="inline-flex items-center gap-0.5 rounded border border-ink-200/90 px-1 py-px text-[7px] font-medium text-ink-600">
          Explore <ArrowTopRightOnSquareIcon className="h-2 w-2" />
        </span>
      </div>
      <div className="px-1.5 py-1">
        <HiresTermsChartSvg />
      </div>
    </div>
  );
}

function WorkforceTable() {
  const { table } = SEMHR_TOUR;
  return (
    <div className="overflow-hidden rounded-lg border border-ink-200/80 bg-white">
      <div className="flex items-center justify-between border-b border-ink-100 px-2 py-1">
        <p className="text-[8px] font-semibold text-ink-800">{table.title}</p>
        <span className="inline-flex items-center gap-0.5 rounded border border-ink-200/90 px-1 py-px text-[7px] font-medium text-ink-600">
          Explore <ArrowTopRightOnSquareIcon className="h-2 w-2" />
        </span>
      </div>
      <table className="w-full text-[7px]">
        <thead>
          <tr className="border-b border-ink-100 bg-ink-50/60 text-[7px] font-semibold uppercase tracking-wide text-ink-500">
            {table.columns.map((col) => (
              <th key={col} className={`px-1.5 py-1 ${col !== 'Month' ? 'text-right' : 'text-left'}`}>{col}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row) => (
            <tr key={row[0]} className="border-b border-ink-50 text-ink-700">
              {row.map((cell, idx) => (
                <td key={cell} className={`px-1.5 py-0.5 ${idx !== 0 ? 'text-right tabular-nums' : ''}`}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="py-1 text-center text-[7px] font-medium text-brand-600">{table.footer}</p>
    </div>
  );
}

function ChatActionIcons() {
  return (
    <div className="flex items-center justify-end gap-1.5 pt-1 text-ink-400">
      <Squares2X2Icon className="h-3 w-3" aria-hidden="true" />
      <ArrowPathIcon className="h-3 w-3" aria-hidden="true" />
      <HandThumbUpIcon className="h-3 w-3" aria-hidden="true" />
      <HandThumbDownIcon className="h-3 w-3" aria-hidden="true" />
    </div>
  );
}

function SqlPanel() {
  const { meta, reasoning, sqlPreview } = SEMHR_TOUR;
  return (
    <div className="overflow-hidden rounded-lg border border-ink-200/80 bg-white">
      <div className="flex items-center justify-between border-b border-ink-100 px-2 py-1">
        <p className="text-[8px] font-semibold text-ink-800">SQL & reasoning</p>
        <span className="text-[7px] font-medium text-emerald-600">{meta.status}</span>
        {/* Product mock: SQL panel "Active" state in the in-app tour, not a marketing wash. */}
      </div>
      <div className="mx-2 mt-1.5 rounded-md bg-ink-50/80 px-2 py-1.5 text-[7px] leading-relaxed text-ink-600">
        {reasoning.map((line) => (<p key={line}>{line}</p>))}
      </div>
      <div className="px-2 py-1.5 text-[7px] text-ink-500">
        <p className="font-semibold uppercase tracking-wide text-ink-400">Details</p>
        <p className="mt-0.5">Agent · {meta.agent}</p>
        <p>Agent label · {meta.agentLabel}</p>
        <p>Semantic model · {meta.model}</p>
        <p>Query ID · {meta.queryId}</p>
      </div>
      <div className="border-t border-ink-100">
        <div className="flex items-center justify-between px-2 py-1">
          <p className="text-[7px] font-semibold uppercase tracking-wide text-ink-400">SQL</p>
          <span className="inline-flex items-center gap-0.5 text-[7px] leading-none text-ink-500">
            <DocumentDuplicateIcon className="h-2.5 w-2.5 shrink-0" /> Copy
          </span>
        </div>
        <pre className="ia-semhr-sql-scroll ia-semhr-scroll-decor mx-2 mb-1.5 max-h-[160px] min-h-[152px] overflow-y-scroll rounded border border-sky-200/15 bg-sky-500/[0.03] px-2 py-1.5 font-mono text-[7px] leading-relaxed text-ink-800">{sqlPreview}</pre>
      </div>
      <div className="px-2 pb-1"><ChatActionIcons /></div>
    </div>
  );
}

function ChatColumn({ isVisible, showAnalyzing, scrollRef, cycle, showSplitToolbar }) {
  const split = Boolean(showSplitToolbar);
  const textXs = split ? 'text-[9px]' : 'text-[8px]';
  const textSm = split ? 'text-[10px]' : 'text-[8px]';
  const showChart = isVisible('chart') || isVisible('table') || isVisible('followups') || isVisible('sql');
  const sqlMode = isVisible('sql');
  const showChartOutput = (isVisible('chart') || isVisible('table') || isVisible('followups')) && !sqlMode;
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-white">
      {showSplitToolbar ? (
        <div className="flex shrink-0 items-center justify-between border-b border-ink-100 px-2 py-1">
          <SegmentedSwitch
            value="split"
            options={[
              { id: 'split', label: 'Split', icon: <Squares2X2Icon className="h-2.5 w-2.5" aria-hidden="true" /> },
              { id: 'chat', label: 'Chat', icon: <ChatBubbleLeftRightIcon className="h-2.5 w-2.5" aria-hidden="true" /> },
            ]}
            className="text-[7px]"
          />
          <ArrowsPointingOutIcon className="h-3 w-3 shrink-0 text-ink-400" aria-hidden="true" />
        </div>
      ) : null}
      <div
        ref={scrollRef}
        className={`ia-semhr-scroll ia-semhr-scroll-decor min-h-0 flex-1 overflow-y-scroll overflow-x-hidden overscroll-contain${split ? '' : ' ia-semhr-chat-scroll--single'}`}
      >
        <div key={cycle} className={`w-full py-1.5 ${split ? 'px-2' : 'ia-semhr-chat-scroll-inner px-2'}`}>
          <div className="space-y-1.5">
            <Step show={isVisible('question')}>
              <div className="flex justify-end">
                <div className={`max-w-[88%] rounded-xl rounded-tr-sm bg-ink-100/80 px-2 py-1.5 leading-snug text-ink-800 ${textSm}`}>{SEMHR_TOUR.question}</div>
              </div>
            </Step>
            {showAnalyzing ? (
              <div className={`ia-semhr-block flex items-center gap-1.5 text-ink-500 ${textSm}`}>
                <span className="h-3 w-3 animate-spin rounded-full border-2 border-brand-200 border-t-brand-500" />
                {SEMHR_TOUR.analyzingText}
              </div>
            ) : null}
            <Step show={isVisible('intro')}>
              <div className="border-l-2 border-brand-400 pl-2">
                <p className={`font-semibold uppercase tracking-wide text-brand-600 ${textXs}`}>Analyst</p>
                <p className={`mt-0.5 leading-relaxed text-ink-700 ${textSm}`}>{SEMHR_TOUR.intro}</p>
              </div>
            </Step>
            <Step show={isVisible('observations')}>
              <div className={`text-ink-700 ${textSm}`}>
                <p className="font-semibold text-ink-800">Key observations:</p>
                <ul className="mt-1 list-disc space-y-0.5 pl-4">
                  {SEMHR_TOUR.observations.map((item) => (
                    <li key={item.lead}><span className="font-semibold">{item.lead}:</span> {item.body}</li>
                  ))}
                </ul>
              </div>
            </Step>
            <Step show={showChart}>
              <ChartSqlToggles sqlMode={sqlMode} />
            </Step>
            <Step show={showChartOutput && isVisible('chart')}><HiresTermsChart /></Step>
            <Step show={showChartOutput && (isVisible('table') || isVisible('followups'))}>
              <WorkforceTable />
            </Step>
            <Step show={showChartOutput && isVisible('followups')}>
              <div className="mt-1 flex flex-wrap gap-1">
                {SEMHR_TOUR.followUps.map((q) => (
                  <span key={q} className="rounded-full border border-ink-200/90 px-1.5 py-0.5 text-[7px] text-ink-600">{q}</span>
                ))}
              </div>
              <ChatActionIcons />
            </Step>
            <Step show={sqlMode}><SqlPanel /></Step>
          </div>
        </div>
      </div>
      <Composer isLoading={showAnalyzing} />
    </div>
  );
}

export default function SemanticHRTourVisual({ compact = false, className = '' }) {
  const chatScrollRef = useRef(null);
  const rightScrollRef = useRef(null);
  const viewportRef = useRef(null);
  const reducedMotion = usePrefersReducedMotion();
  useTourViewportScale(viewportRef);
  const { ref, inView } = useInView(0.12);
  const { isVisible, visibleCount, cycle, resetting } = usePlayback(
    inView,
    { steps: SEMHR_TOUR_STEPS, stepDelays: SEMHR_TOUR_DELAYS, loopPause: SEMHR_TOUR_LOOP_PAUSE },
    reducedMotion
  );

  useSmoothCoworkScroll(chatScrollRef, visibleCount, reducedMotion, { cycle, resetting });

  const showLanding = !isVisible('question');
  const showSplit = isVisible('dash-termination') || isVisible('bookmark-open-headcount') || isVisible('bookmark-pick-headcount') || isVisible('dash-headcount-loading') || isVisible('dash-headcount') || isVisible('bookmark-open-hires') || isVisible('bookmark-pick-hires') || isVisible('dash-hires-loading') || isVisible('dash-hires') || isVisible('dynamic-empty') || isVisible('dynamic-prompt') || isVisible('heatmap') || isVisible('line-edit') || isVisible('line-chart');
  const currentStep = getCurrentStep(visibleCount);
  const dashView = resolvePbiDashboardView(currentStep);
  const showAnalyzing = isVisible('analyzing') && !isVisible('intro');
  const dynamicPhase = resolveDynamicPhase(isVisible);
  const bookmarkState = resolvePbiBookmarkState(currentStep);

  const atDynamicEmpty = isVisible('dynamic-empty');
  const atDynamicPrompt = isVisible('dynamic-prompt');
  const atHeatmap = isVisible('heatmap');
  const atLineEdit = isVisible('line-edit');
  const atLineChart = isVisible('line-chart');
  const showDynamicScroll = atDynamicEmpty || atDynamicPrompt || atHeatmap || atLineEdit || atLineChart;

  const dvScrollPhase = atLineChart || atLineEdit
    ? 'line'
    : atHeatmap
      ? 'heatmap'
      : atDynamicEmpty || atDynamicPrompt
        ? 'entry'
        : null;

  useEffect(() => {
    if (showDynamicScroll) return;
    const el = rightScrollRef.current;
    if (el) el.scrollTop = 0;
  }, [visibleCount, cycle, showDynamicScroll, dashView]);

  useStableDvScroll(
    rightScrollRef,
    dvScrollPhase,
    Boolean(showSplit && showDynamicScroll),
    reducedMotion,
  );

  const dashOpen = Boolean(showSplit && dashView);

  return (
    <div
      ref={ref}
      className={`ia-semhr-tour ia-semhr-tour-frame pointer-events-none select-none overflow-hidden rounded-xl border border-ink-200 bg-white shadow-sm ${compact ? 'text-[8px]' : ''} ${className}`}
      role="img"
      aria-label="Mock-up of the Semantic HR product tour: a copilot chat answers workforce questions beside Power BI headcount, hires and termination dashboards"
    >
      <div aria-hidden="true" className="contents">
      <div ref={viewportRef} className="ia-semhr-tour-viewport">
        <div className="ia-semhr-tour-scale">
          <TopBar />
          <div className="ia-semhr-tour-body">
            <Sidebar />
            <div className={`ia-semhr-tour-main bg-white${dashOpen ? ' ia-semhr-tour-main--split' : ''}`}>
              <div className={`ia-semhr-tour-chat-col flex min-h-0 min-w-0 flex-col overflow-hidden${dashOpen ? ' border-r border-ink-200/80' : ' ia-semhr-tour-chat-col--single'}`}>
                <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
                  <div className={showLanding && !dashOpen ? 'invisible flex min-h-0 flex-1 flex-col overflow-hidden' : 'flex min-h-0 flex-1 flex-col overflow-hidden'}>
                    <ChatColumn
                      isVisible={isVisible}
                      showAnalyzing={showAnalyzing}
                      scrollRef={chatScrollRef}
                      cycle={cycle}
                      showSplitToolbar={dashOpen}
                    />
                  </div>
                  {showLanding && !dashOpen ? (
                    <div className="absolute inset-0 z-10 flex min-h-0 flex-col overflow-hidden bg-white">
                      <LandingPanel />
                      <Composer />
                    </div>
                  ) : null}
                </div>
              </div>
              <div
                className="ia-semhr-tour-dash-slot flex flex-col"
                aria-hidden={!dashOpen}
              >
                {dashView ? (
                  <SemanticHRPbiPanel
                    view={dashView}
                    scrollRef={rightScrollRef}
                    dynamicPhase={dynamicPhase}
                    bookmarkState={bookmarkState}
                    showDynamicSection={dashOpen}
                  />
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
